/**
 * トピックサンバーストマップコンポーネント
 * ECharts Sunburstを使用してMECEトピック構造を可視化
 */

import React from 'react';
import { Card, Skeleton, Empty, Typography, Row, Col, Statistic, Tooltip, message } from 'antd';
import ReactECharts from 'echarts-for-react';
import {
  BulbOutlined,
  BarChartOutlined,
  TrophyOutlined,
  InfoCircleOutlined,
  ApartmentOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useTopicMapData } from './hooks/useTopicMapData';

const { Title, Text } = Typography;

/**
 * トピックサンバーストマップコンポーネント
 * @param {Object} props - コンポーネントプロパティ
 * @param {Object} props.competitorData - 競合分析データ
 * @param {boolean} props.loading - ローディング状態
 * @param {Object} props.style - カスタムスタイル
 * @param {number} props.height - チャートの高さ
 * @param {boolean} props.showStatistics - 統計情報の表示フラグ
 * @param {boolean} props.enableAnimation - アニメーション有効フラグ
 * @param {function} props.onTopicClick - トピッククリック時のコールバック
 */
const TopicSunburstMap = ({
  competitorData,
  loading = false,
  style = {},
  height = 500,
  showStatistics = true,
  enableAnimation = true,
  onTopicClick,
  // 追加: 完了時のみ"データなし"表示にするためのフラグ
  isCompleted = false,
}) => {
  // トピックマップデータを処理
  const {
    chartOption,
    statistics,
    hasData,
    isEmpty,
    isLoading,
    onChartClick,
    extractedData,
  } = useTopicMapData(competitorData, {
    height,
    enableAnimation,
    showVisualMap: true,
  });

  // クリックイベントハンドラー
  const handleChartClick = params => {
    onChartClick(params);

    // 外部コールバックがある場合は実行
    if (onTopicClick) {
      onTopicClick(params.data);
    }
  };

  // トピック構造をコピー
  const copyTopicStructure = () => {
    if (!extractedData || !extractedData.categories || extractedData.categories.length === 0) {
      message.warning('コピーするデータがありません');
      return;
    }

    const lines = [];
    extractedData.categories.forEach(category => {
      lines.push(`- ${category.category_name}`);
      if (category.unified_topics && category.unified_topics.length > 0) {
        category.unified_topics.forEach(topic => {
          // トピック名を抽出（文字列またはオブジェクト対応）
          const topicName = typeof topic === 'string' 
            ? topic 
            : (topic.name || topic.topic || topic.topic_name || 'トピック名なし');
          
          // スコアを取得（extractedData.topicScoresから検索）
          const topicScore = extractedData.topicScores?.find(
            score => (score.topic || score.name || score.topic_name) === topicName
          );
          const avgScore = topicScore?.average_score;
          const scoreText = avgScore !== undefined && avgScore !== null ? ` (${avgScore.toFixed(1)})` : '';
          lines.push(`  - ${topicName}${scoreText}`);
        });
      }
    });

    const text = lines.join('\n');
    navigator.clipboard.writeText(text);
    message.success('トピック構造をコピーしました');
  };

  // ローディング状態の表示
  if (loading || isLoading) {
    return (
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BulbOutlined style={{ color: '#1890ff' }} />
            <span>トピックマップ</span>
          </div>
        }
        style={{ marginBottom: '24px', ...style }}
      >
        <div
          style={{
            height: `${height}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </Card>
    );
  }

  // データが空の場合の表示
  if (isEmpty || !hasData) {
    // 分析未完了中は"データなし"ではなくスケルトンを表示
    if (!isCompleted) {
      return (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BulbOutlined style={{ color: '#1890ff' }} />
              <span>トピックマップ</span>
            </div>
          }
          style={{ marginBottom: '24px', ...style }}
        >
          <div
            style={{
              height: `${height}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        </Card>
      );
    }
    return (
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BulbOutlined style={{ color: '#1890ff' }} />
            <span>トピックマップ</span>
          </div>
        }
        style={{ marginBottom: '24px', ...style }}
      >
        <div
          style={{
            height: `${height}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <Text type="secondary">トピックデータがありません</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  分析が完了するとトピック構造が表示されます
                </Text>
              </div>
            }
          />
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ApartmentOutlined style={{ color: '#1890ff' }} />
          <span>トピック構造マップ</span>
          {extractedData.mainQuery && extractedData.mainQuery !== 'メインクエリ' && (
            <Text
              type="secondary"
              style={{ fontSize: '12px', marginLeft: '8px' }}
            >
              {extractedData.mainQuery}
            </Text>
          )}
        </div>
      }
      extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="sunburst-description" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              色の濃さがスコアの高さを表します
            </Text>
          </div>
          {hasData && (
            <Tooltip title="トピック構造をコピー">
              <CopyOutlined
                onClick={copyTopicStructure}
                style={{ color: '#1890ff', cursor: 'pointer', fontSize: '14px' }}
              />
            </Tooltip>
          )}
        </div>
      }
      style={{ marginBottom: '24px', ...style }}
    >
      {/* Sunburstチャート */}
      <div className="topic-sunburst-container" style={{ height: `${height}px`, width: '100%', minHeight: '600px' }}>
        <ReactECharts
          option={chartOption}
          style={{ height: '100%', width: '100%' }}
          onEvents={{
            click: handleChartClick,
          }}
          opts={{
            renderer: 'canvas',
            useDirtyRect: false,
            width: 'auto',
            height: 'auto',
          }}
        />
      </div>

      {/* 使い方セクションは非表示に変更 */}
    </Card>
  );
};

export default TopicSunburstMap;
